import React from 'react';
import { Layers, Layout, Settings, ArrowRight } from 'lucide-react';
import ElementsPanel from './ElementsPanel';
import AssessmentItemsPanel from './AssessmentItemsPanel';
import PropertiesPanel from './PropertiesPanel';
import LayoutPanel from './LayoutPanel';
import NavigationPanel from './NavigationPanel';

const SidebarPanel = ({
  position, // 'left' | 'right' | 'bottom'
  sidebarWidth,
  activePanel,
  setActivePanel,
  selectedElement,
  selectedElements,
  onDeleteSelected,
  handleSidebarResizeStart,
  handleSidebarResizeKeyDown,
  // Elements Panel props
  setDraggingFromSidebar,
  setDragPreviewPosition,
  // Assessment Items Panel props
  items,
  screens,
  currentScreenIndex,
  setShowItemBank,
  addElementToScreen,
  removeAssessmentItem,
  removeAllItems,
  onEditItem,
  // Properties Panel props
  selectedElementData,
  setScreens,
  setConfirmDialog,
  calculateQuestionHeight,
  updateElementContent,
  distributeOptions,
  alignOptions,
  toggleLockElement,
  isElementLocked,
  deleteElement,
  ungroupQuestion,
  regroupQuestion,
  updateSubElementPosition,
  // Layout Panel props
  buttonTemplates,
  selectedButtonTemplate,
  handleLoadButtonTemplate,
  handleSaveButtonTemplate,
  handleDeleteButtonTemplate,
  handleApplyTemplateToAllScreens,
  newTemplateName,
  setNewTemplateName,
  templateSaveLevel,
  setTemplateSaveLevel,
  setTemplateAsDefault,
  setSetTemplateAsDefault,
  updateElementPosition,
  updateElementSize,
  allTemplatesCache,
  refreshTemplatesCache,
  appliedDefaultTemplateIds,
  // Navigation Panel props
  navigationRules,
  setNavigationRules,
  setShowRuleEditorModal,
  setEditingRule,
  // Assessment ID for template fetching
  assessmentId,
  // Device mode for calculating centered positions + canvas preset (Layout tab)
  deviceMode,
  deviceDimensions,
  handleDeviceModeChange,
  // Selection handlers for navigation panel
  setSelectedElement,
  setCurrentScreenIndex,
  // Manual-save handler so LayoutPanel bulk-apply can persist screens (FECOA-1337)
  handleSave,
  // Callback when a default template is set from LayoutPanel
  onDefaultTemplateSet,
  // Form-wide formatting (font family, base size, line spacing) — FECOA-732
  globalFormatting,
  onGlobalFormattingChange,
}) => {
  const isBottom = position === 'bottom';
  const isLeft = position === 'left';
  const isRight = position === 'right';

  const tabs = [
    { id: 'elements', label: 'Elements', icon: Layers, alwaysEnabled: true },
    { id: 'layout', label: 'Layout', icon: Layout, alwaysEnabled: true },
    { id: 'properties', label: 'Properties', icon: Settings, alwaysEnabled: true },
    { id: 'navigation', label: 'Navigation', icon: ArrowRight, alwaysEnabled: true },
  ];

  return (
    <div 
      className={`sidebar-panel bg-white dark:bg-gray-900 border-gray-200/80 dark:border-white/[0.06] flex z-10 relative overflow-hidden ${
        isBottom ? 'h-64 border-t flex-row overflow-x-auto' : 
        isLeft ? 'border-r flex-col' : 
        'border-l flex-col'
      }`}
      style={!isBottom ? { width: `${sidebarWidth}px` } : {}}
    >
      {/* Sidebar Tabs */}
      <div className={`bg-white dark:bg-gray-900 border-b border-gray-200/80 dark:border-white/[0.06] flex ${
        isBottom ? 'flex-col border-r border-gray-200/80 dark:border-white/[0.06] min-w-fit' : 'flex-row w-full px-1'
      }`}>
        {tabs.map(({ id, label, icon: Icon, alwaysEnabled }) => {
          const isActive = activePanel === id;
          const isDisabled = !alwaysEnabled && !selectedElement;
          
          return (
            <button
              key={id}
              onClick={() => setActivePanel(id)}
              disabled={isDisabled}
              className={`${isBottom ? 'py-3 px-3' : 'flex-1 pt-3 pb-2.5 px-2'} text-[12.5px] font-medium transition-colors duration-150 relative ${
                isActive
                  ? 'text-gray-900 dark:text-white'
                  : isDisabled 
                    ? 'text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              <div className="mx-auto mb-1 w-fit">
                <Icon className={`w-4 h-4 ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} strokeWidth={1.75} />
              </div>
              <span className={isBottom ? 'text-[11px]' : ''}>{label}</span>
              {isActive && <div className={`absolute bg-primary-600 dark:bg-primary-400 ${isBottom ? 'right-0 top-2 bottom-2 w-0.5 rounded-full' : 'bottom-0 left-3 right-3 h-0.5 rounded-full'}`} />}
            </button>
          );
        })}
      </div>

      {/* Panel Content */}
      <div className="flex-1 overflow-y-auto overflow-x-auto bg-gray-50 dark:bg-gray-950/40">
        {activePanel === 'elements' ? (
          <div className="p-4 space-y-5">
            <ElementsPanel
              setDraggingFromSidebar={setDraggingFromSidebar}
              setDragPreviewPosition={setDragPreviewPosition}
            />
            <AssessmentItemsPanel
              items={items}
              screens={screens}
              currentScreenIndex={currentScreenIndex}
              selectedElement={selectedElement}
              setSelectedElement={setSelectedElement}
              setShowItemBank={setShowItemBank}
              setDraggingFromSidebar={setDraggingFromSidebar}
              setDragPreviewPosition={setDragPreviewPosition}
              addElementToScreen={addElementToScreen}
              removeAssessmentItem={removeAssessmentItem}
              removeAllItems={removeAllItems}
              onEditItem={onEditItem}
            />
          </div>
        ) : activePanel === 'layout' ? (
          <LayoutPanel
            selectedElementData={selectedElementData}
            selectedElement={selectedElement}
            screens={screens}
            currentScreenIndex={currentScreenIndex}
            setScreens={setScreens}
            buttonTemplates={buttonTemplates}
            selectedButtonTemplate={selectedButtonTemplate}
            handleLoadButtonTemplate={handleLoadButtonTemplate}
            handleSaveButtonTemplate={handleSaveButtonTemplate}
            handleDeleteButtonTemplate={handleDeleteButtonTemplate}
            handleApplyTemplateToAllScreens={handleApplyTemplateToAllScreens}
            newTemplateName={newTemplateName}
            setNewTemplateName={setNewTemplateName}
            templateSaveLevel={templateSaveLevel}
            setTemplateSaveLevel={setTemplateSaveLevel}
            setTemplateAsDefault={setTemplateAsDefault}
            setSetTemplateAsDefault={setSetTemplateAsDefault}
            updateElementPosition={updateElementPosition}
            updateElementSize={updateElementSize}
            calculateQuestionHeight={calculateQuestionHeight}
            distributeOptions={distributeOptions}
            alignOptions={alignOptions}
            assessmentId={assessmentId}
            deviceMode={deviceMode}
            deviceDimensions={deviceDimensions}
            handleDeviceModeChange={handleDeviceModeChange}
            allTemplatesCache={allTemplatesCache}
            refreshTemplatesCache={refreshTemplatesCache}
            appliedDefaultTemplateIds={appliedDefaultTemplateIds}
            handleSave={handleSave}
            onDefaultTemplateSet={onDefaultTemplateSet}
            globalFormatting={globalFormatting}
            onGlobalFormattingChange={onGlobalFormattingChange}
          />
        ) : activePanel === 'navigation' ? (
          <NavigationPanel
            selectedElementData={selectedElementData}
            selectedElement={selectedElement}
            screens={screens}
            currentScreenIndex={currentScreenIndex}
            setScreens={setScreens}
            navigationRules={navigationRules}
            setNavigationRules={setNavigationRules}
            setShowRuleEditorModal={setShowRuleEditorModal}
            setEditingRule={setEditingRule}
            setConfirmDialog={setConfirmDialog}
            setSelectedElement={setSelectedElement}
            setCurrentScreenIndex={setCurrentScreenIndex}
          />
        ) : activePanel === 'properties' ? (
          (selectedElements?.size ?? 0) > 1 ? (
            // Multi-select: show a compact group panel instead of the
            // single-element editor (FECOA-1523).
            <div className="p-4">
              <div className="card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="w-4 h-4 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
                <h3 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">
                  {selectedElements.size} items selected
                </h3>
              </div>
              <p className="text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400 mb-4">
                Drag any selected item to move the group. Use the arrow keys to nudge,
                Ctrl/Cmd+C then Ctrl/Cmd+V to copy, or delete them all at once.
              </p>
              <button
                onClick={onDeleteSelected}
                className="btn btn-danger btn-sm w-full"
              >
                Delete selected
              </button>
              </div>
            </div>
          ) : (
          <PropertiesPanel
            selectedElementData={selectedElementData}
            selectedElement={selectedElement}
            screens={screens}
            currentScreenIndex={currentScreenIndex}
            setScreens={setScreens}
            setConfirmDialog={setConfirmDialog}
            calculateQuestionHeight={calculateQuestionHeight}
            updateElementContent={updateElementContent}
            distributeOptions={distributeOptions}
            alignOptions={alignOptions}
            toggleLockElement={toggleLockElement}
            isElementLocked={isElementLocked}
            deleteElement={deleteElement}
            ungroupQuestion={ungroupQuestion}
            regroupQuestion={regroupQuestion}
            updateSubElementPosition={updateSubElementPosition}
          />
          )
        ) : null}
      </div>
      
      {/* Resize Handle */}
      {!isBottom && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          aria-valuenow={sidebarWidth}
          aria-valuemin={320}
          aria-valuemax={600}
          tabIndex={0}
          className={`absolute top-0 bottom-0 w-1.5 cursor-col-resize bg-transparent hover:bg-primary-500/40 focus-visible:bg-primary-500 focus-visible:outline-none transition-colors z-50 group ${
            isLeft ? 'right-0' : 'left-0'
          }`}
          onMouseDown={handleSidebarResizeStart}
          onKeyDown={handleSidebarResizeKeyDown}
          title="Drag or use arrow keys to resize"
        >
          {/* Invisible hit area, biased toward the canvas so it doesn't cover the sidebar scrollbar */}
          <div className={`absolute inset-y-0 w-10 ${isLeft ? 'right-0 -mr-7' : 'left-0 -ml-7'}`} />
          <div className={`absolute top-1/2 -translate-y-1/2 w-1 h-10 bg-gray-300 dark:bg-white/20 opacity-80 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity rounded-full ${
            isLeft ? 'right-0' : 'left-0'
          }`} />
        </div>
      )}
    </div>
  );
};

export default SidebarPanel;
